import { StatusPill, type StatusPillStatus } from '../../components/ui';
import { cn } from '../../lib/cn';
import { formatHours, formatPercent } from '../../lib/format';
import type { BankStatus, SupportSeriesPoint, SupportStatus } from './api';

// ---------------------------------------------------------------------------
// Status pill mapping
// ---------------------------------------------------------------------------

const STATUS_META: Record<SupportStatus, { pill: StatusPillStatus; label: string }> = {
  ok: { pill: 'success', label: 'On track' },
  warning: { pill: 'warning', label: 'Near limit' },
  over: { pill: 'error', label: 'Over' },
};

/** The pill's words, for anywhere a pill cannot go — CSV, aria labels. */
export function supportStatusLabel(status: SupportStatus | null): string {
  return status ? STATUS_META[status].label : 'No allowance';
}

export function SupportStatusPill({ status }: { status: SupportStatus | null }) {
  if (!status) return <StatusPill status="neutral" label="No allowance" dot={false} />;
  const meta = STATUS_META[status];
  return <StatusPill status={meta.pill} label={meta.label} />;
}

// ---------------------------------------------------------------------------
// 12-month sparkline — one bar per month, colored by burn status.
// ---------------------------------------------------------------------------

const BAR_W = 5;
const GAP = 3;
const H = 28;

function barClass(point: SupportSeriesPoint): string {
  if (point.allowance === null || point.pct === null) return 'fill-grey-300';
  if (point.pct > 1) return 'fill-status-late';
  if (point.pct >= 0.8) return 'fill-warning';
  return 'fill-success';
}

export function monthLabel(key: string): string {
  const [y, m] = key.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, 15)).toLocaleDateString('en-US', {
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

const SHORT_MONTH = (key: string): string =>
  new Intl.DateTimeFormat('en-US', { month: 'short', timeZone: 'UTC' }).format(
    new Date(`${key}-01T12:00:00Z`),
  );

/** Bar colour for a burn ratio — the same three states the status pills use. */
function burnColor(pct: number | null): string {
  if (pct === null) return '#d4d4d4';
  if (pct > 1) return '#dc2626';
  if (pct >= 0.8) return '#f59e0b';
  return '#22c55e';
}

/** A month still running is never drawn at the same weight as a closed one. */
function burnPaint(color: string, partial: boolean): React.CSSProperties {
  return partial
    ? {
        background: `repeating-linear-gradient(45deg, ${color} 0 3px, #ffffff 3px 6px)`,
        boxShadow: `inset 0 0 0 1px ${color}`,
      }
    : { background: color };
}

/**
 * Every client's burn, summed. The per-client sparkline answers "is this client
 * creeping over"; nothing answered "are we, overall" — and the KPI cards only
 * ever show one month. Same encoding as the sparkline, one size up, with the
 * month's total allowance drawn as the line the bar has to stay under, so the
 * verdict is position as well as colour.
 */
export function SupportBurnChart({
  series,
  partialMonth,
}: {
  series: SupportSeriesPoint[];
  /** YYYY-MM of the month still running — hatched, and never a verdict. */
  partialMonth?: string;
}) {
  const max = Math.max(1, ...series.map((p) => Math.max(p.used, p.allowance ?? 0))) * 1.1;
  return (
    <div className="flex h-40 items-stretch gap-1.5">
      {series.map((point) => {
        const partial = point.month === partialMonth;
        const label = `${monthLabel(point.month)}: ${formatHours(point.used, { unit: true })} used${
          point.allowance !== null
            ? ` of ${formatHours(point.allowance, { unit: true })} allowed (${formatPercent(
                point.pct ?? 0,
                { decimals: 0 },
              )})`
            : ', no allowance in force'
        }${partial ? ' — month to date, still running' : ''}`;
        return (
          <div key={point.month} className="flex min-w-0 flex-1 flex-col gap-1">
            <div className="relative min-h-0 flex-1" role="img" aria-label={label} title={label}>
              <div
                className="absolute bottom-0 left-1/2 w-[46%] min-w-[10px] max-w-[44px] -translate-x-1/2 rounded-t-sm"
                style={{
                  height: `${Math.max((point.used / max) * 100, point.used > 0 ? 1.5 : 0)}%`,
                  ...burnPaint(burnColor(point.pct), partial),
                }}
              />
              {point.allowance !== null && point.allowance > 0 && (
                // The ceiling for that month's bar — wider than the bar so it
                // reads as a line to stay under, not as part of the bar.
                <div
                  className="absolute inset-x-[16%] h-[2px] rounded-full bg-secondary/60"
                  style={{ bottom: `${(point.allowance / max) * 100}%` }}
                />
              )}
            </div>
            {/* Twelve columns in 330px leaves 22px each — the initial only, the
                same convention the budget heat strip uses at that width. */}
            <span className="truncate text-center text-caption text-grey-500">
              <span className="sm:hidden">{SHORT_MONTH(point.month).charAt(0)}</span>
              <span className="hidden sm:inline">{SHORT_MONTH(point.month)}</span>
            </span>
          </div>
        );
      })}
    </div>
  );
}

/** Mini used-vs-allowance history. Bars scale to the series max; months with
 *  no allowance render grey. */
export function Sparkline({ series }: { series: SupportSeriesPoint[] }) {
  const width = series.length * (BAR_W + GAP) - GAP;
  const max = Math.max(1, ...series.map((p) => Math.max(p.used, p.allowance ?? 0)));
  return (
    <svg
      width={width}
      height={H}
      viewBox={`0 0 ${width} ${H}`}
      className="overflow-visible"
      role="img"
      aria-label="Support hours, last 12 months"
    >
      {series.map((p, i) => {
        const h = p.used > 0 ? Math.max(2, (p.used / max) * (H - 2)) : 1.5;
        const title =
          p.allowance !== null
            ? `${monthLabel(p.month)}: ${formatHours(p.used)} of ${formatHours(p.allowance)} h`
            : `${monthLabel(p.month)}: ${formatHours(p.used)} h (no allowance)`;
        return (
          <g key={p.month}>
            <rect
              x={i * (BAR_W + GAP)}
              y={H - h}
              width={BAR_W}
              height={h}
              rx={1}
              className={p.used > 0 ? barClass(p) : 'fill-grey-200'}
            >
              <title>{title}</title>
            </rect>
          </g>
        );
      })}
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Hour banks — the primary support model
// ---------------------------------------------------------------------------

// Bands are percentages of the PURCHASE (Jesse, 2026-08-20): over 50% left is
// Good, 10–50% is OK, 10% or under is Low — then the absolute states.
const BANK_META: Record<BankStatus, { pill: StatusPillStatus; label: string }> = {
  good: { pill: 'success', label: 'Good' },
  ok: { pill: 'neutral', label: 'OK' },
  low: { pill: 'warning', label: 'Low' },
  exhausted: { pill: 'error', label: 'Exhausted' },
  overage: { pill: 'error', label: 'Overage' },
};

/** The pill's words, for anywhere a pill cannot go — CSV, aria labels. */
export function bankStatusLabel(status: BankStatus | null): string {
  return status ? BANK_META[status].label : 'No bank';
}

export function BankStatusPill({ status }: { status: BankStatus | null }) {
  if (!status) return <StatusPill status="neutral" label="No bank" dot={false} />;
  const meta = BANK_META[status];
  return <StatusPill status={meta.pill} label={meta.label} />;
}

/**
 * How full the bank is. The bar fills from EMPTY, not from spent — the question
 * a support lead asks is "how much is left", so that is what the ink shows.
 */
export function BalanceBar({
  pct,
  status,
  className,
}: {
  pct: number | null;
  status: BankStatus | null;
  className?: string;
}) {
  const filled = pct === null ? 0 : Math.max(0, Math.min(1, pct));
  const tone =
    status === 'overage' || status === 'exhausted'
      ? 'bg-status-late'
      : status === 'low'
        ? 'bg-warning'
        : 'bg-success';
  return (
    <span
      className={cn('inline-flex h-1.5 w-full overflow-hidden rounded-full bg-grey-200', className)}
      role="img"
      aria-label={pct === null ? 'No bank' : `${Math.round(filled * 100)}% of the bank remaining`}
    >
      <span className={cn('h-full rounded-full', tone)} style={{ width: `${filled * 100}%` }} />
    </span>
  );
}

/** "in 24 days" / "today" / "8 days ago" — one phrasing for every bank date. */
/**
 * "in 22 days" · "in 8 months" · "in 14 years".
 *
 * ⚠ A DAY COUNT STOPS BEING INFORMATION LONG BEFORE IT STOPS BEING ACCURATE.
 * A dormant client burning 0.02 h a week renders a real, correct projection of
 * *"in 5208 days"* — which is not a number anybody reads, and which sits beside
 * a date whose year the column does not print. Past two months the answer is
 * months; past two years it is years, because that is all the precision a burn
 * rate averaged over twelve weeks can honestly carry.
 */
export function relativeDays(days: number | null): string {
  if (days === null) return '—';
  if (days === 0) return 'today';
  const abs = Math.abs(days);
  const unit =
    abs < 60
      ? `${abs} ${abs === 1 ? 'day' : 'days'}`
      : abs < 730
        ? `${Math.round(abs / 30)} months`
        : `${Math.round(abs / 365)} years`;
  return days > 0 ? `in ${unit}` : `${unit} ago`;
}

/** A threshold as the words the alert used: "25% left" / "exhausted". */
export function thresholdLabel(threshold: number): string {
  return threshold <= 0 ? 'exhausted' : `${Math.round(threshold * 100)}% left`;
}
